import { MouseEvent, useContext, useState } from 'react';
import Button from 'components/global/Button/Primary';
import Icon from 'components/global/Icon/Primary';
import { CartContext } from 'context-providers/cart-provider';
import { ProductsContext } from 'context-providers/products-provider';
import { trackButtonEvent } from 'lib/analytics';
import { PRODUCT_SKUS } from 'lib/constants/product-skus';
import { formatMoney } from 'lib/utils/format-money';
import { getPromoAmountText } from 'lib/utils/coupons/apply-promo-code-discount';
import { getMultiOrderCount } from 'lib/utils/coupons/get-multi-order-count';
import type { ProductPromoCode } from 'lib/utils/coupons/resolve-promo-code';
import {
  Body,
  BodyNote,
  BodyText,
  CheckList,
  CheckListItem,
  Content,
  Details,
  Highlight,
  HighlightedBodyText,
  PlanPrice,
  PlanTitle,
  PriceBlock,
  StrikePrice,
  TitleRow,
  Toggle,
  ToggleButton,
  ToggleRow,
  Wrapper,
} from './styles';

type OfferPlan = 'subscription' | 'one-time';

export interface OtpOfferSelectorProps {
  /** What the first subscription order charges, after any promo or sale. */
  firstOrderPrice: number;
  /** The regular monthly subscription price. */
  regularPrice: number;
  oneTimePrice: number;
  /** Only pass a promo that already discounts the subscription in this market. */
  promoCode?: ProductPromoCode | null;
  subscriptionHref: string;
  onStartSubscription: (event: MouseEvent<HTMLAnchorElement>) => void;
}

const ONE_TIME_HREF = `/cart?cart=${PRODUCT_SKUS.DAILY_SYNBIOTIC_ONE_TIME}`;

const SUBSCRIPTION_PERKS = [
  'Glass jar + travel vial welcome kit',
  'Free US shipping',
  '30-day risk-free guarantee',
  'Pause or cancel anytime',
];

// How many orders the promo covers, or null for codes that apply to every order
const getPromoDuration = (promoCode: ProductPromoCode) => {
  const orderCount = getMultiOrderCount(promoCode);
  if (orderCount) return `first ${orderCount} months`;
  if (promoCode.recurringCycleLimit === 1) return 'first month';
  return null;
};

const getSavingsPercent = (price: number, oneTimePrice: number) =>
  Math.floor(((oneTimePrice - price) / oneTimePrice) * 100);

const OtpOfferSelector = ({
  firstOrderPrice,
  regularPrice,
  oneTimePrice,
  promoCode = null,
  subscriptionHref,
  onStartSubscription,
}: OtpOfferSelectorProps) => {
  const { openCart } = useContext(CartContext);
  const { currencyCode } = useContext(ProductsContext);
  const [plan, setPlan] = useState<OfferPlan>('subscription');
  const isSubscription = plan === 'subscription';

  const selectPlan = (nextPlan: OfferPlan) => {
    if (nextPlan === plan) return;
    setPlan(nextPlan);
    trackButtonEvent(
      nextPlan === 'one-time' ? 'Toggle - Buy Once' : 'Toggle - Subscription',
      window.location.pathname,
      {},
    );
  };

  const handleStartOneTime = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    trackButtonEvent('Start Now - Buy Once', window.location.pathname, {});
    openCart({ skus: [PRODUCT_SKUS.DAILY_SYNBIOTIC_ONE_TIME] });
  };

  const promoAmountText = getPromoAmountText(promoCode, currencyCode);
  const hasPromo = !!promoCode && !!promoAmountText;
  // Total savings against the one-time price, promo included
  const totalSavingsPercent = getSavingsPercent(firstOrderPrice, oneTimePrice);
  const subscriberSavingsPercent = getSavingsPercent(
    regularPrice,
    oneTimePrice,
  );
  const subscriberSavingsText =
    subscriberSavingsPercent > 0
      ? `${subscriberSavingsPercent}% off every order`
      : null;
  const perks =
    hasPromo && subscriberSavingsText
      ? [subscriberSavingsText, ...SUBSCRIPTION_PERKS]
      : SUBSCRIPTION_PERKS;

  const renderPlanBody = () => {
    if (!isSubscription) {
      return (
        <Body>
          <BodyText>30-day supply</BodyText>
        </Body>
      );
    }
    if (hasPromo) {
      const promoDuration = getPromoDuration(promoCode);
      return (
        <Body>
          <HighlightedBodyText>
            <Highlight $tall>
              {`Extra ${promoAmountText} off with ${promoCode.code.toUpperCase()}`}
            </Highlight>
          </HighlightedBodyText>
          {promoDuration && <BodyNote>{promoDuration}</BodyNote>}
        </Body>
      );
    }
    if (!subscriberSavingsText) return null;
    return (
      <Body>
        <HighlightedBodyText>
          <Highlight $tall>{subscriberSavingsText}</Highlight>
        </HighlightedBodyText>
        <StrikePrice>{formatMoney(oneTimePrice, currencyCode)}</StrikePrice>
      </Body>
    );
  };

  return (
    <Wrapper>
      <ToggleRow>
        <Toggle role='group' aria-label='Choose a purchase option'>
          <ToggleButton
            type='button'
            $selected={isSubscription}
            aria-pressed={isSubscription}
            onClick={() => selectPlan('subscription')}
          >
            {totalSavingsPercent > 0 ? (
              <>
                {'Subscribe + '}
                <Highlight>{`Save ${totalSavingsPercent}%`}</Highlight>
              </>
            ) : (
              'Subscribe'
            )}
          </ToggleButton>
          <ToggleButton
            type='button'
            $selected={!isSubscription}
            aria-pressed={!isSubscription}
            onClick={() => selectPlan('one-time')}
          >
            Buy Once
          </ToggleButton>
        </Toggle>
      </ToggleRow>
      <Details>
        <Content>
          <PriceBlock>
            <TitleRow>
              <PlanTitle>
                {isSubscription ? 'Delivered Monthly' : 'Delivered Once'}
              </PlanTitle>
              <PlanPrice
                data-testid={
                  isSubscription
                    ? 'otp-offer-subscription-price'
                    : 'otp-offer-one-time-price'
                }
              >
                {formatMoney(
                  isSubscription ? firstOrderPrice : oneTimePrice,
                  currencyCode,
                )}
              </PlanPrice>
            </TitleRow>
            {renderPlanBody()}
          </PriceBlock>
        </Content>
        <Button
          href={isSubscription ? subscriptionHref : ONE_TIME_HREF}
          onClick={isSubscription ? onStartSubscription : handleStartOneTime}
          size='md'
        >
          Start Now
        </Button>
        {isSubscription && (
          <CheckList>
            {perks.map((perk) => (
              <CheckListItem key={perk}>
                <Icon name='checkmark' size='md' />
                <span>{perk}</span>
              </CheckListItem>
            ))}
          </CheckList>
        )}
      </Details>
    </Wrapper>
  );
};

export default OtpOfferSelector;
